import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { DOCUMENT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Modal from './ModalRelative';
import {
    getDistanceFromLatLonInMile,
    numberFormatter,
    isSupportedPhotoFormat,
} from '../../utils/Utils';
import { displayDate, displayTimeWithTimezone } from '../../utils/date-time';
import {
    Content,
    Row,
    Label,
    Value,
    Title,
    ModalWrap,
    ModalHeader,
} from './MetadataModal';

export default function PhotoMetadataModal(props) {
    const metadata = props.metadata?.metadata || {};
    const variant = props.variant || 'modal'; // 'modal' | 'drawer'

    const showReusedPhoto =
        !metadata && isSupportedPhotoFormat(metadata && metadata.format);
    const photoIntegrity = props.photoIntegrity; // work_order only.
    const noValue = 'N/A';
    const timezoneId =
        metadata.timezoneId || props.document?.property?.timezone || 'UTC';
    const invalidCreateDateTime =
        props.document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER
            ? photoIntegrity?.validateCreationDate?.validCreationDate === false
            : false;

    let withInRadius;
    if (
        metadata.latitude &&
        metadata.longitude &&
        props.document.property.standardized &&
        props.document.property.latitude &&
        props.document.property.longitude
    ) {
        withInRadius =
            getDistanceFromLatLonInMile(
                metadata.latitude,
                metadata.longitude,
                props.document.property.latitude,
                props.document.property.longitude
            ) <= 0.25;
    }
    const invalidLocation = withInRadius === false;
    const address =
        (withInRadius
            ? props.document.property.stdAddress
            : metadata?.photoAddress) ||
        'Address is not available or not a valid address';

    const content = (
        <Content>
            <Row>
                <Label>FILE SIZE:</Label>
                <Value>
                    {metadata.bytes
                        ? `${numberFormatter.format(metadata.bytes)} BYTES`
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label>Type:</Label>
                <Value>
                    {metadata.format ? metadata.format.toUpperCase() : noValue}
                </Value>
            </Row>
            <Row>
                <Label>MODEL:</Label>
                <Value>
                    {metadata.model
                        ? `${metadata.make ?? ''} ${metadata.model}`
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label invalid={invalidCreateDateTime}>CREATE DATE:</Label>
                <Value>
                    {metadata.createDate
                        ? displayDate(
                              moment.tz(
                                  metadata.createDate,
                                  ['YYYY:MM:DD HH:mm:ss', moment.ISO_8601],
                                  timezoneId
                              ),
                              { invalidDisplay: 'N/A' }
                          )
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label invalid={invalidCreateDateTime}>TIME:</Label>
                <Value>
                    {metadata.createDate
                        ? displayTimeWithTimezone(
                              moment.tz(
                                  metadata.createDate,
                                  ['YYYY:MM:DD HH:mm:ss', moment.ISO_8601],
                                  timezoneId
                              )
                          )
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label>IMAGE SIZE:</Label>
                <Value>
                    {metadata.height && metadata.width
                        ? `${metadata.height}X${metadata.width}`
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label invalid={invalidLocation}>GPS LATITUDE:</Label>
                <Value>{metadata.gpsLatitude || noValue}</Value>
            </Row>
            <Row>
                <Label invalid={invalidLocation}>GPS LONGITUDE:</Label>
                <Value>{metadata.gpsLongitude || noValue}</Value>
            </Row>
            <Row>
                <Label invalid={invalidLocation}>ADDRESS:</Label>
                <Value style={{ width: 135, whiteSpace: 'normal' }}>
                    {address}
                </Value>
            </Row>
            {showReusedPhoto && props.metadata.reused.length > 1 && (
                <Row>
                    <Label invalid>REUSED PHOTO:</Label>
                    <Value>
                        <div
                            style={{
                                color: '#57c1e9',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                            }}
                            onClick={() =>
                                props.handleReusedViewOnClick(props.metadata)
                            }
                        >
                            VIEW
                        </div>
                    </Value>
                </Row>
            )}
            <Row style={{ display: 'none' }}>
                <Label>HASH ID</Label>
                <Value>
                    {props.metadata.hashId
                        ? `${props.metadata.hashId}`
                        : noValue}
                </Value>
            </Row>
        </Content>
    );
    if (variant === 'drawer') {
        return (
            <ModalWrap>
                <ModalHeader>
                    <Title>{props.title ?? 'Photo Metadata'}</Title>
                    <IconButton
                        aria-label='Close'
                        onClick={props.handleCloseOnClick}
                        size='small'
                    >
                        <CloseIcon />
                    </IconButton>
                </ModalHeader>
                {content}
            </ModalWrap>
        );
    }

    return (
        <div>
            <Modal
                controlId='photo-metadata-modal'
                contentLabel='photo-metadata-modal'
                onCloseClick={props.handleCloseOnClick}
                showClose
                closeByDocument
                header='Photo Metadata'
                style={{ ...props.style, width: '320px', position: 'absolute' }}
            >
                {content}
            </Modal>
        </div>
    );
}
PhotoMetadataModal.propTypes = {
    metadata: PropTypes.shape({
        metadata: PropTypes.shape({
            bytes: PropTypes.number,
            createDate: PropTypes.string,
            format: PropTypes.string,
            gpsLatitude: PropTypes.string,
            gpsLongitude: PropTypes.string,
            height: PropTypes.number,
            model: PropTypes.string,
            width: PropTypes.number,
            timezoneId: PropTypes.string,
            latitude: PropTypes.number,
            longitude: PropTypes.number,
            photoAddress: PropTypes.string,
        }),
        reused: PropTypes.arrayOf(PropTypes.string),
        hashId: PropTypes.string,
        photoAddress: PropTypes.string,
    }),
    photoIntegrity: PropTypes.shape({
        validateCreationDate: PropTypes.shape({
            validCreationDate: PropTypes.bool,
        }),
    }),
    document: PropTypes.shape({
        documentTypeId: PropTypes.string,
        property: PropTypes.shape({
            latitude: PropTypes.number,
            longitude: PropTypes.number,
            stdAddress: PropTypes.string,
            timezone: PropTypes.string,
            standardized: PropTypes.bool,
        }),
    }),
    handleCloseOnClick: PropTypes.func,
    handleReusedViewOnClick: PropTypes.func,
    style: PropTypes.shape({
        modal: PropTypes.shape({
            display: PropTypes.string,
            position: PropTypes.string,
            zIndex: PropTypes.number,
            paddingTop: PropTypes.string,
            left: PropTypes.number,
            top: PropTypes.number,
            width: PropTypes.string,
            height: PropTypes.string,
            overflow: PropTypes.string,
            backgroundColor: PropTypes.string,
        }),
    }),
    variant: PropTypes.oneOf(['modal', 'drawer']),
};
